import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate, useNavigate } from "react-router-dom";
import { fmtError } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
    const { user, loading, login } = useAuth();
    const [email, setEmail] = useState("");
    const [pw, setPw] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const navigate = useNavigate();

    if (loading) return null;
    if (user) return <Navigate to="/" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const u = await login(email, pw);
            toast.success(`Welcome, ${u.name.split(" ")[0]}`);
            navigate("/");
        } catch (e) {
            setErr(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2 bg-white">
            {/* Left — brand panel */}
            <div className="hidden md:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
                <div className="flex items-center gap-3">
                    <span className="w-9 h-9 bg-white text-slate-900 flex items-center justify-center font-display font-bold text-lg">P</span>
                    <div className="flex flex-col">
                        <span className="font-display text-lg tracking-tight">Pulse</span>
                        <span className="overline" style={{ color: "#9ca3af", fontSize: 10 }}>Campaign Operations</span>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="overline" style={{ color: "#9ca3af" }}>// 2026 / Edition 01</div>
                    <h1 className="font-display text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
                        One control room.<br />
                        Every campaign,<br />
                        every team.
                    </h1>
                    <p className="text-slate-300 text-base leading-relaxed max-w-md">
                        Replace inbox chaos with a structured workflow — from intake to invoicing — for client servicing, execution, delivery, reporting, and accounts.
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-px border-t border-slate-700">
                    {[
                        { k: "Campaigns", v: "Live" },
                        { k: "Approvals", v: "1-click" },
                        { k: "Tenants", v: "Multi" },
                    ].map((s, i) => (
                        <div key={i} className="pt-4">
                            <div className="overline" style={{ color: "#9ca3af" }}>{s.k}</div>
                            <div className="font-mono text-2xl mt-1">{s.v}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right — login form */}
            <div className="flex flex-col justify-center px-6 md:px-16 py-12">
                <div className="md:hidden mb-6 flex items-center gap-2">
                    <span className="w-8 h-8 bg-slate-900 text-white flex items-center justify-center font-display font-bold">P</span>
                    <span className="font-display text-lg">Pulse</span>
                </div>

                <div className="max-w-md w-full mx-auto md:mx-0">
                    <div className="overline mb-2">Sign in</div>
                    <h2 className="font-display text-3xl tracking-tight mb-1">Access your workspace</h2>
                    <p className="text-slate-500 mb-8 text-sm">Use your agency credentials to continue.</p>

                    <form onSubmit={onSubmit} className="space-y-5">
                        <div>
                            <Label htmlFor="email" className="overline">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email-input"
                                className="rounded-none mt-1 border-slate-300 focus-visible:ring-0 focus-visible:border-slate-900 h-11"
                                placeholder="you@agency.com"
                            />
                        </div>
                        <div>
                            <Label htmlFor="pw" className="overline">Password</Label>
                            <Input
                                id="pw"
                                type="password"
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                required
                                data-testid="login-password-input"
                                className="rounded-none mt-1 border-slate-300 focus-visible:ring-0 focus-visible:border-slate-900 h-11"
                                placeholder="••••••••"
                            />
                        </div>

                        {err && (
                            <div
                                data-testid="login-error"
                                className="text-xs font-mono uppercase tracking-wider bg-red-50 text-red-700 border border-red-200 px-3 py-2"
                            >
                                {err}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={busy}
                            data-testid="login-submit-button"
                            className="w-full h-11 rounded-none bg-slate-900 hover:bg-slate-800 text-white font-medium gap-2"
                        >
                            {busy ? "Signing in…" : "Sign in"}
                            <ArrowRight size={16} weight="bold" />
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
